import { useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import type { ServiceProvider, MapBounds } from '@/types';
import { ProviderPopup } from './ProviderPopup';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet with bundlers
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

// Custom marker icons
const defaultIcon = new L.Icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const highlightedIcon = new L.Icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [30, 49],
  iconAnchor: [15, 49],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
  className: 'highlighted-marker',
});

// Custom cluster icon
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createClusterCustomIcon = (cluster: any) => {
  const count = cluster.getChildCount();
  let size = 'small';
  let dimensions = 36;

  if (count >= 10 && count < 50) {
    size = 'medium';
    dimensions = 44;
  } else if (count >= 50) {
    size = 'large';
    dimensions = 52;
  }

  return L.divIcon({
    html: `<div class="cluster-icon cluster-icon-${size}"><span>${count}</span></div>`,
    className: 'custom-cluster-marker',
    iconSize: L.point(dimensions, dimensions, true),
  });
};

interface MapViewProps {
  providers: ServiceProvider[];
  center?: { lat: number; lng: number };
  zoom?: number;
  onBoundsChange?: (bounds: MapBounds) => void;
  selectedProviderId?: string | null;
  onProviderSelect?: (provider: ServiceProvider | null) => void;
  hoveredProviderId?: string | null;
  /** If true, map will fly to provider when selected. Disable for embedded maps where clicking opens popup in place. */
  flyToSelected?: boolean;
  /** If true, map will auto-fit to show all providers */
  fitBounds?: boolean;
}

// Component to handle dynamic center changes (for geolocation)
function CenterHandler({ center }: { center?: { lat: number; lng: number } }) {
  const map = useMap();
  const prevCenter = useRef<{ lat: number; lng: number } | undefined>(undefined);

  useEffect(() => {
    if (center && (!prevCenter.current || prevCenter.current.lat !== center.lat || prevCenter.current.lng !== center.lng)) {
      map.flyTo([center.lat, center.lng], 12, {
        duration: 1.5,
      });
      prevCenter.current = center;
    }
  }, [center, map]);

  return null;
}

// Component to handle map events and auto-fit bounds
function MapEventHandler({
  onBoundsChange,
  selectedProviderId,
  providers,
  flyToSelected = true,
  fitBounds = false,
}: {
  onBoundsChange?: (bounds: MapBounds) => void;
  selectedProviderId?: string | null;
  providers: ServiceProvider[];
  flyToSelected?: boolean;
  fitBounds?: boolean;
}) {
  const map = useMap();
  const hasFitBounds = useRef(false);
  const prevProvidersLength = useRef(0);

  // Handle bounds change
  useMapEvents({
    moveend: () => {
      if (onBoundsChange) {
        const bounds = map.getBounds();
        onBoundsChange({
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
        });
      }
    },
  });

  // Auto-fit bounds to show all providers
  useEffect(() => {
    if (!fitBounds) return;

    const validProviders = providers.filter((p) => p.latitude && p.longitude);

    // Only fit bounds if we have providers and either:
    // 1. We haven't fit bounds yet, or
    // 2. The number of providers changed significantly
    if (
      validProviders.length > 0 &&
      (!hasFitBounds.current || Math.abs(validProviders.length - prevProvidersLength.current) > 2)
    ) {
      const bounds = L.latLngBounds(
        validProviders.map((p) => [p.latitude!, p.longitude!] as [number, number])
      );

      // Add some padding
      map.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: 14,
        animate: hasFitBounds.current, // Only animate after initial fit
      });

      hasFitBounds.current = true;
      prevProvidersLength.current = validProviders.length;
    }
  }, [providers, map, fitBounds]);

  // Fly to selected provider (only if flyToSelected is enabled)
  useEffect(() => {
    if (flyToSelected && selectedProviderId) {
      const provider = providers.find((p) => p.id === selectedProviderId);
      if (provider?.latitude && provider?.longitude) {
        map.flyTo([provider.latitude, provider.longitude], 14, {
          duration: 1,
        });
      }
    }
  }, [selectedProviderId, providers, map, flyToSelected]);

  return null;
}

export function MapView({
  providers,
  center = { lat: 39.8283, lng: -98.5795 }, // Center of US
  zoom = 4,
  onBoundsChange,
  selectedProviderId,
  onProviderSelect,
  hoveredProviderId,
  flyToSelected = true,
  fitBounds = false,
}: MapViewProps) {
  // Memoize markers to prevent unnecessary re-renders
  const markers = useMemo(() => {
    return providers
      .filter((provider) => provider.latitude && provider.longitude)
      .map((provider) => {
        const isHovered = hoveredProviderId === provider.id;
        const isSelected = selectedProviderId === provider.id;

        return (
          <Marker
            key={provider.id}
            position={[provider.latitude!, provider.longitude!]}
            icon={isHovered || isSelected ? highlightedIcon : defaultIcon}
            eventHandlers={{
              click: () => onProviderSelect?.(provider),
            }}
          >
            <Popup>
              <ProviderPopup provider={provider} />
            </Popup>
          </Marker>
        );
      });
  }, [providers, hoveredProviderId, selectedProviderId, onProviderSelect]);

  return (
    <MapContainer
      center={[center.lat, center.lng]}
      zoom={zoom}
      style={{ width: '100%', height: '100%' }}
      scrollWheelZoom={true}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      {/* Handle dynamic center changes (e.g., from geolocation) */}
      <CenterHandler center={center} />

      <MapEventHandler
        onBoundsChange={onBoundsChange}
        selectedProviderId={selectedProviderId}
        providers={providers}
        flyToSelected={flyToSelected}
        fitBounds={fitBounds}
      />

      <MarkerClusterGroup
        chunkedLoading
        iconCreateFunction={createClusterCustomIcon}
        maxClusterRadius={60}
        spiderfyOnMaxZoom
        showCoverageOnHover={false}
        zoomToBoundsOnClick
        animate
      >
        {markers}
      </MarkerClusterGroup>
    </MapContainer>
  );
}
